import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import MenuDropdown from 'src/modules/shared/components/molecules/dropdowns/menu-dropdown';
import { SiWhatsapp } from 'react-icons/si';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { NewCallForm } from '../calls/new-call-form';
import { useGlobalPhoneContext } from 'src/contexts/global-phone-context';
import { PiPhoneLight, PiPlusLight, PiUploadSimple } from 'react-icons/pi';
import { WhatsAppTemplateModal } from 'src/modules/messaging/whatsapp/components/organisms/templates/template-modal';
import { Typography } from '@material-tailwind/react';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { TestConversationPermission } from 'src/modules/shared/types/permissions.type';
import { CreateTestConversationModal } from '../../test-conversations/create-test-conversation-modal';
import { TestConversationIcon } from 'src/modules/shared/components/atoms/icons/custom-icons/test-conversation-icon';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

interface InteractionsActionsDropdownProps {
  funnelId: number;
  onConversationCreated?: (conversationId: number) => void;
}

export const InteractionsActionsDropdown = ({
  funnelId,
  onConversationCreated,
}: InteractionsActionsDropdownProps) => {
  const [isNewCallOpen, setIsNewCallOpen] = useState(false);
  const [isWhatsAppTemplateOpen, setIsWhatsAppTemplateOpen] = useState(false);
  const [isCreateTestConversationOpen, setIsCreateTestConversationOpen] =
    useState(false);
  const { setGlobalPhoneVisible } = useGlobalPhoneContext();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { hasPermission: canManageTestConversations } = useHasPermissions(
    TestConversationPermission.Manage
  );

  // Check for bulk conversation creation permission
  const { hasPermission: canCreateBulkConversation } = useHasPermissions(
    'bulk-create:crm-funnel-conversation'
  );

  const baseActionItems = [
    {
      icon: <PiPhoneLight className="h-4 w-4" />,
      content: t('communications.interactions.actions.newCall'),
      onClick: () => {
        setGlobalPhoneVisible(true);
        setIsNewCallOpen(true);
      },
    },
    {
      icon: <SiWhatsapp className="h-4 w-4" />,
      content: t('communications.interactions.actions.newMessage'),
      onClick: () => {
        setIsWhatsAppTemplateOpen(true);
      },
    },
    // {
    //   icon: <PiUserPlusLight className="h-4 w-4" />,
    //   content: 'Nuevo contacto',
    //   onClick: () => {
    //     /* TODO: Implement new contact */
    //   },
    // },
  ];

  // Add import option only if user has permission
  const actionItems = [...baseActionItems];

  if (canManageTestConversations) {
    actionItems.push({
      icon: <TestConversationIcon className="h-[1.05rem] w-[1.05rem]" />,
      content: t('communications.testConversations.actions.create'),
      onClick: () => {
        setIsCreateTestConversationOpen(true);
      },
    });
  }

  if (canCreateBulkConversation) {
    actionItems.push({
      icon: <PiUploadSimple className="h-4 w-4" />,
      content: t('communications.interactions.actions.importConversations'),
      onClick: () => {
        void navigate({
          to: '/communications/import',
          search: { phoneVersion: 'v1' },
        });
      },
    });
  }

  return (
    <>
      <MenuDropdown
        label=""
        items={actionItems}
        icon={<PiPlusLight className="h-5 w-5" />}
        size="sm"
        variant="primary"
        placement="bottom-start"
        className="rounded-lg"
      />

      <Modal
        isOpen={isNewCallOpen}
        onClose={() => setIsNewCallOpen(false)}
        header={
          <Typography className="font-medium">
            {t('communications.interactions.actions.newCall')}
          </Typography>
        }
        body={<NewCallForm onClose={() => setIsNewCallOpen(false)} />}
        dialogClassName="sm:max-w-md"
      />

      <WhatsAppTemplateModal
        isOpen={isWhatsAppTemplateOpen}
        onClose={() => setIsWhatsAppTemplateOpen(false)}
      />

      <CreateTestConversationModal
        isOpen={isCreateTestConversationOpen}
        onClose={() => setIsCreateTestConversationOpen(false)}
        funnelId={funnelId}
        onCreated={(conversationId) => {
          onConversationCreated?.(conversationId);
          setIsCreateTestConversationOpen(false);
        }}
      />
    </>
  );
};
